import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type {
  RSSFeedDataPodcast,
  RSSFeedDataPodcastItem,
  RSSFeedDataYouTube,
  RSSFeedDataYouTubeEntry,
  RSSFeedSearchResult,
  TRSSFeedSearchElement,
} from '@shared/dream-components';
import {
  formatDate,
  getEpisodeUrl,
  normalizeToArray,
  RSSFeedSearchElement,
  RSSFeedSearchModalElement,
  stripHtmlTags,
} from '@shared/dream-components';
import { ClientOnly } from 'remix-utils/client-only';

import useRssFeed from '~/hooks/useRssFeed';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TRSSFeedSearchElement;
  children: ReactNode | ReactNode[];
}

function getYouTubeEpisodeUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

const RSSFeedSearch = ({ node, children }: Props) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const url = node.attrs.url || '';
  const source = node.attrs.source || 'podcast';

  // Use data from node attrs (SSR) or fetch if not available
  const { data: rssFeed, isLoading } = useRssFeed({ url });
  const feedData = rssFeed || node.attrs.data;

  const rssFeedMap = site?.viewable_site_version?.customization?.rss_feed_map;
  const rssFeedUUID = rssFeedMap?.[url]?.uuid;

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback((open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setSearch('');
    }
  }, []);

  const results: RSSFeedSearchResult[] = useMemo(() => {
    if (!feedData) return [];

    const lowerSearch = search.toLowerCase().trim();

    if (source === 'youtube') {
      const data = feedData as RSSFeedDataYouTube;
      const entries = normalizeToArray(data?.feed?.entry);

      return entries
        .filter((entry: RSSFeedDataYouTubeEntry) => {
          if (!lowerSearch) return true;
          const title = entry.title?.toLowerCase() || '';
          const description = entry['media:group']?.['media:description']?.toLowerCase() || '';
          return title.includes(lowerSearch) || description.includes(lowerSearch);
        })
        .map((entry: RSSFeedDataYouTubeEntry) => ({
          id: entry['yt:videoId'] || entry.id || '',
          title: entry.title || '',
          description: entry['media:group']?.['media:description'] || '',
          imageUrl: entry['media:group']?.['media:thumbnail']?.['@url'],
          url: getYouTubeEpisodeUrl(entry['yt:videoId'] || ''),
          date: entry.published ? formatDate(entry.published) : '',
        }));
    }

    // Podcast source
    const data = feedData as RSSFeedDataPodcast;
    const items = normalizeToArray(data?.rss?.channel?.item);

    return items
      .filter((item: RSSFeedDataPodcastItem) => {
        if (!lowerSearch) return true;
        const title = item.title?.toLowerCase() || '';
        const description = stripHtmlTags(item.description || '').toLowerCase();
        return title.includes(lowerSearch) || description.includes(lowerSearch);
      })
      .map((item: RSSFeedDataPodcastItem) => ({
        id: item.guid || `${item.title}-${item.pubDate}` || '',
        title: item.title || '',
        description: item.description || '',
        imageUrl:
          item['itunes:image']?.['@href'] ||
          data?.rss?.channel?.image?.url ||
          data?.rss?.channel?.['itunes:image']?.['@href'],
        url: getEpisodeUrl(item.title || '', rssFeedUUID || ''),
        date: item.pubDate ? formatDate(item.pubDate) : '',
      }));
  }, [feedData, search, source, rssFeedUUID]);

  return (
    <>
      <RSSFeedSearchElement
        element={{
          type: 'rss_feed_search',
          attrs: node.attrs,
          content: [{ id: 'empty-children', text: '' }],
        }}
        onClick={handleOpenModal}
      >
        {children}
      </RSSFeedSearchElement>
      <ClientOnly fallback={null}>
        {() => (
          <RSSFeedSearchModalElement
            open={isModalOpen}
            setOpen={handleCloseModal}
            search={search}
            setSearch={setSearch}
            results={results}
            isLoading={isLoading && isModalOpen}
            placeholder={node.attrs.placeholder || 'Search episodes...'}
          />
        )}
      </ClientOnly>
    </>
  );
};

export default RSSFeedSearch;
